import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button, Input, toast } from "@aurelia/ui";
import { useLogin, useVerifyPin, useSetupPin, useGoogleAuth } from "../../features/auth/mutations";
import type { LoginResponse } from "../../features/auth/mutations";
import { useAuthStore } from "../../lib/stores/auth";
import { useStoreConfig } from "../../features/config/queries";
import { useQueryClient } from "@tanstack/react-query";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { GoogleSignInButton } from "../../components/auth/GoogleSignInButton";

type Step = "credentials" | "pin" | "pin-setup";

export function LoginPage() {
  useDocumentMeta("Sign In", "Sign in to your Latif General Store account.");
  const [step, setStep] = useState<Step>("credentials");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [pinError, setPinError] = useState("");

  const login = useLogin();
  const googleAuth = useGoogleAuth();
  const verifyPin = useVerifyPin();
  const setupPin = useSetupPin();
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: config } = useStoreConfig();
  const whatsappNumber = config?.whatsappNumber?.replace(/[^\d]/g, "");
  const forgotPasswordHref = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        `Hi, I forgot my password.${identifier ? ` My account: ${identifier}` : ""} Can you help me reset it?`
      )}`
    : undefined;

  const goToDestination = (user: { role: string }) => {
    navigate(user.role === "ADMIN" || user.role === "SUPER_ADMIN" ? "/admin" : "/account");
  };

  const handleLoginResult = (result: LoginResponse) => {
    if (result.status === "success") {
      setUser(result.user);
      // Not invalidateQueries: see useRegister's onSuccess in mutations.ts —
      // a stale pre-login "/auth/me" request can resolve after this and
      // clobber the user we just set. We already have the fresh result.
      queryClient.setQueryData(["me"], { user: result.user });
      goToDestination(result.user);
      return;
    }

    setChallengeToken(result.challengeToken);
    setStep(result.status === "pin_required" ? "pin" : "pin-setup");
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await login.mutateAsync({ identifier, password });
    handleLoginResult(result);
  };

  const handleGoogleCredential = async (idToken: string) => {
    try {
      const result = await googleAuth.mutateAsync(idToken);
      handleLoginResult(result);
    } catch (err) {
      toast((err as Error).message ?? "Google sign-in failed", "danger");
    }
  };

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError("");
    const result = await verifyPin.mutateAsync({ challengeToken, pin });
    goToDestination(result.user);
  };

  const handleSetupPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError("");
    if (pin !== confirmPin) {
      setPinError("PINs don't match");
      return;
    }
    const result = await setupPin.mutateAsync({ challengeToken, pin });
    goToDestination(result.user);
  };

  if (step === "pin" || step === "pin-setup") {
    const isSetup = step === "pin-setup";
    const pending = isSetup ? setupPin.isPending : verifyPin.isPending;
    const error = pinError || (isSetup ? setupPin.error : verifyPin.error) as Error | undefined | string;

    return (
      <main className="min-h-screen flex items-center justify-center bg-canvas px-6">
        <form
          onSubmit={isSetup ? handleSetupPin : handleVerifyPin}
          className="w-full max-w-sm flex flex-col gap-5"
        >
          <h1 className="text-display-sm text-center mb-2">{isSetup ? "Set up your PIN" : "Enter your PIN"}</h1>
          <p className="text-sm text-ink-muted text-center -mt-3">
            {isSetup
              ? "Create a 6-digit PIN to secure admin access. You'll enter this every time you sign in."
              : "For admin access, enter your 6-digit security PIN."}
          </p>
          <Input
            label="PIN"
            name="pin"
            type="password"
            inputMode="numeric"
            maxLength={6}
            autoComplete="off"
            required
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          {isSetup && (
            <Input
              label="Confirm PIN"
              name="confirmPin"
              type="password"
              inputMode="numeric"
              maxLength={6}
              autoComplete="off"
              required
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          )}
          {error && (
            <p role="alert" className="text-sm text-red-500">
              {typeof error === "string" ? error : error.message}
            </p>
          )}
          <Button type="submit" size="lg" disabled={pending || pin.length !== 6} className="w-full">
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {pending ? "Verifying…" : isSetup ? "Set PIN & continue" : "Continue"}
          </Button>
          {pending && (
            <p className="text-xs text-ink-muted text-center -mt-2">
              This can take a few seconds on a slow connection — please don't refresh.
            </p>
          )}
          <button
            type="button"
            onClick={() => setStep("credentials")}
            className="text-sm text-ink-muted hover:text-ink text-center"
          >
            ← Back
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-canvas px-6">
      <form onSubmit={handleCredentialsSubmit} className="w-full max-w-sm flex flex-col gap-5">
        <h1 className="text-display-sm text-center mb-2">Welcome back</h1>
        <Input
          label="Email or phone number"
          type="text"
          name="identifier"
          autoComplete="username"
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {forgotPasswordHref && (
          <a
            href={forgotPasswordHref}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-accent hover:underline -mt-2 self-end"
          >
            Forgot password?
          </a>
        )}
        {login.isError && (
          <p role="alert" className="text-sm text-red-500">
            {(login.error as Error).message}
          </p>
        )}
        <Button type="submit" size="lg" disabled={login.isPending} className="w-full">
          {login.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {login.isPending ? "Signing in…" : "Sign in"}
        </Button>
        {login.isPending && (
          <p className="text-xs text-ink-muted text-center -mt-2">
            This can take a few seconds on a slow connection — please don't refresh.
          </p>
        )}
        <div className="flex items-center gap-3 -my-1">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-ink-muted">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <GoogleSignInButton onCredential={handleGoogleCredential} />
        <p className="text-sm text-ink-muted text-center">
          New here? <Link to="/register" className="text-accent hover:underline">Create an account</Link>
        </p>
      </form>
    </main>
  );
}
